import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Typography, Paper, Box, Divider,
  CircularProgress, IconButton, Tooltip, Chip, Button
} from '@mui/material';
import { MenuBook as DocsIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useTranslation } from 'react-i18next';
import { getDocumentationTree, IDocNode, loadExtendedDoc } from '../../common/i-doc/i-doc.documentation';
import { buildMarkdownExport, downloadMarkdown } from '../../common/i-doc/i-doc.export';
import { PageIndices } from '../../common/i-doc/i-doc.registry';
import { DocumentationSidebar } from './DocumentationSidebar';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Same colors as IDocInspector's on-page overlay: page — синій, tab — зелений, modal — червоний.
const IDOC_TYPE_COLOR: Record<string, 'primary' | 'success' | 'error' | 'default'> = {
  page: 'primary',
  tab: 'success',
  modal: 'error',
  section: 'default',
};

const DOCUMENTATION_ROLES = ['expert', 'client', 'admin', 'pm'];

export const DocumentationPage: React.FC = () => {
  const { t } = useTranslation(['hints', 'common', 'routes']);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Сторінка лише для продакта: він бачить документацію всіх кабінетів незалежно від власних ролей.
  const userRoles = DOCUMENTATION_ROLES;

  const initialRole = searchParams.get('role');
  const initialId = searchParams.get('id');

  const [role, setRole] = useState<string>(
    initialRole && userRoles.includes(initialRole) ? initialRole : userRoles[0]
  );
  const [selectedNode, setSelectedNode] = useState<IDocNode | null>(null);
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    if (!userRoles.includes(role)) {
      setRole(userRoles[0]);
    }
  }, [userRoles, role]);

  const tree = useMemo(() => getDocumentationTree(role), [role]);

  useEffect(() => {
    if (initialId && !selectedNode) {
        const allNodes: IDocNode[] = [];
        tree.groups.forEach(g => {
            g.nodes.forEach(n => {
                allNodes.push(n);
                if (n.children) allNodes.push(...n.children);
            });
        });
        
        const found = allNodes.find(n => n.id === initialId);
        if (found) {
            setSelectedNode(found);
        }
    }
  }, [tree, initialId]);

  useEffect(() => {
    const params: Record<string, string> = { role };
    if (selectedNode) {
        params.id = selectedNode.id;
    }
    setSearchParams(params, { replace: true });
  }, [role, selectedNode, setSearchParams]);

  useEffect(() => {
    const loadMarkdown = async () => {
      if (!selectedNode) {
        setMarkdown(null);
        return;
      }

      setLoading(true);
      try {
        const content = await loadExtendedDoc(selectedNode.id);
        setMarkdown(content);
      } catch (e) {
        console.error('Failed to load markdown', e);
        setMarkdown(null);
      } finally {
        setLoading(false);
      }
    };

    loadMarkdown();
  }, [selectedNode]);

  const handleNodeClick = (node: IDocNode) => {
    setSelectedNode(node);
  };

  const handleRoleChange = (_event: React.MouseEvent<HTMLElement>, newRole: string | null) => {
    if (newRole !== null) {
      setRole(newRole);
      setSelectedNode(null);
    }
  };

  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const roleLabel = t(`sidebar.mode.${role}`, { ns: 'translation', defaultValue: role });
      const markdown = await buildMarkdownExport(
        tree,
        (labelKey) => t(labelKey),
        (id) => t(`hints:${id.replace(/:/g, '.')}`, { defaultValue: '' }),
        roleLabel,
      );
      downloadMarkdown(`everyday-documentation-${role}.md`, markdown);
    } finally {
      setExporting(false);
    }
  };

  const renderHint = (id: string) => {
    const hintKey = id.replace(/:/g, '.');
    const hintText = t(`hints:${hintKey}`, { defaultValue: '' });
    if (!hintText) return null;

    return (
      <Paper sx={{ p: 2, bgcolor: 'info.lighter', borderLeft: '4px solid', borderColor: 'info.main', mb: 3 }}>
        <Typography variant="subtitle2" color="info.main" gutterBottom>
          Базова підказка (Hint):
        </Typography>
        <Typography variant="body2">
          {hintText}
        </Typography>
      </Paper>
    );
  };

  const rightAction = (
    <Tooltip title="Зміст документації">
        <IconButton 
            onClick={() => setMobileSidebarOpen(true)}
            color="inherit"
            sx={{ display: { md: 'none' } }}
        >
            <DocsIcon />
        </IconButton>
    </Tooltip>
  );

  return (
    <DashboardLayout rightAction={rightAction} disableContentPadding data-i-doc={PageIndices.DOCUMENTATION}>
      <Box sx={{
        display: 'flex',
        mr: { xs: 0, md: '200px' },
        minHeight: 'calc(100vh - 52px)',
        bgcolor: 'background.default',
      }}>
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
            <Paper sx={{ p: { xs: 2, md: 4 }, minHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 3 }}>
                    {selectedNode && (
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                            <Chip
                                label={selectedNode.id}
                                size="small"
                                color={IDOC_TYPE_COLOR[selectedNode.type] ?? 'default'}
                                sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                            />
                            {selectedNode.type === 'page' && selectedNode.route && (
                                <Button
                                    component="a"
                                    href={selectedNode.route}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="small"
                                    startIcon={<OpenInNewIcon fontSize="small" />}
                                >
                                    Відкрити сторінку
                                </Button>
                            )}
                        </Box>
                    )}
                    <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                        {selectedNode ? t(selectedNode.label) : 'Документація Everyday Studio'}
                    </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {!selectedNode ? (
                    <Box sx={{ textAlign: 'center', py: 10 }}>
                        <Typography variant="h6" color="text.secondary">
                            Оберіть розділ у меню змісту для перегляду документації
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                    {renderHint(selectedNode.id)}

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : markdown ? (
                        <Box sx={{ 
                            '& h1': { fontSize: { xs: '1.5rem', md: '2rem' }, mb: 2 },
                            '& h2': { fontSize: { xs: '1.25rem', md: '1.5rem' }, mt: 4, mb: 1 },
                            '& h3': { fontSize: { xs: '1.1rem', md: '1.2rem' }, mt: 3, mb: 1 },
                            '& p': { mb: 2, lineHeight: 1.6 },
                            '& ul, & ol': { mb: 2, pl: 4 },
                            '& li': { mb: 1 },
                            '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                            '& code': { bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontFamily: 'monospace' }
                        }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {markdown}
                        </ReactMarkdown>
                        </Box>
                    ) : (
                        <Box sx={{ mt: 4, p: 4, border: '1px dashed', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                Розширена документація для цього елемента ще не створена.
                            </Typography>
                        </Box>
                    )}
                    </Box>
                )}
            </Paper>
        </Container>

        <DocumentationSidebar 
            tree={tree}
            selectedNode={selectedNode}
            onNodeClick={handleNodeClick}
            role={role}
            userRoles={userRoles}
            onRoleChange={handleRoleChange}
            isMobileOpen={mobileSidebarOpen}
            onClose={() => setMobileSidebarOpen(false)}
            onExport={handleExport}
            exporting={exporting}
        />
      </Box>
    </DashboardLayout>
  );
};

export default DocumentationPage;
